import React, { createContext, useState, useEffect, useContext, useCallback } from "react";
import { StorageItem } from "../types/storageItem.types";
import { getStorageItems } from "../services/storageItemService";
import { useOrderContext } from "./OrderContext";
import { isDateFarEnough, extractGapDays } from "../utils/calendar";
import { usePublicCatalog } from "./PublicCatalogContext";
import useCatalogFetch from "../hooks/useCatalogFetch";
/**
* Shape of the StorageItem Context value.
*/
interface StorageItemContextType {
    /** Array of storage items fetched from the backend */
    items: StorageItem[];
    /** Loading flag indicating if the storage items are being fetched */
    loading: boolean;
    /** Error message if loading fails, otherwise null */
    error: string | null;
    reloadItems: () => void
}

/**
* React context to provide storage items data, loading status, and error state.
*/
const StorageItemContext = createContext<StorageItemContextType>({
    items: [],
    loading: true,
    error: null,
    reloadItems: () => {}
});

/**
* Provider component that fetches storage items from the backend,
* manages loading and error state, and provides this data to children.
* 
* @param children - React child elements
*/
export const StorageItemProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [items, setItems] = useState<StorageItem[]>([]); // Storage items
    const [loading, setLoading] = useState(true); // Loading state 
    const [error, setError] = useState<string | null>(null); // Errors
    const { isPublicView } = usePublicCatalog();
    const { orderData } = useOrderContext();
    
    const filterItems = useCallback((data: StorageItem[]) => {
        if (isPublicView) return data;
        const selectedDate = orderData?.selectedDate ?? "";
        return data.filter((p: StorageItem) => {
            const gap = extractGapDays((p as any).order_in_advance);
            if (gap == null) return true;
            return isDateFarEnough(gap, selectedDate);
        });
    }, [isPublicView, orderData?.selectedDate]);

    const { reload: reloadItems } = useCatalogFetch<StorageItem>({
        fetcher: getStorageItems,
        onData: setItems,
        setLoading,
        setError,
        filterData: filterItems,
        logLabel: "storage item",
    });

    useEffect(() => {
        reloadItems();
    }, [reloadItems]);

    return (
        <StorageItemContext.Provider value={{ items, loading, error, reloadItems }}>
            {children}
        </StorageItemContext.Provider>
    );
};
/**
* Hook to access the StorageItemContext.
* 
* @returns StorageItemContextType object with storage items, loading flag, and error message
*/
export const useStorageItems = () => useContext(StorageItemContext);
